import { AxiosError } from "axios";
import { isBefore, parseISO } from "date-fns";
import * as Sentry from "@sentry/react";
import { logError } from "../config";
import {
    CustomError,
    DistributionRedemption,
    UserSession,
    parseToCustomError
} from '../dtos';

const ignoreStatusCodes: number[] = [200, 201, 204, 400, 401, 403, 404];

type ExceptionDetail = {
    err: unknown;
    defaultMessage?: string;
    payload: object;
};


type ExceptionSessionDetail = {
    err: unknown;
    defaultMessage?: string;
    user: UserSession | null;
};

type ExceptionLandingPageDetail = {
    err: unknown;
    action: 'LOAD_DISTRIBUTION' | 'LOAD_VOUCHERS' | 'PDF_DOWNLOAD';
    distributionRedemption: DistributionRedemption | null;
    codes: string[];
};

const getMessageError = ({ payload, err, defaultMessage }: ExceptionDetail): CustomError => {
    if (defaultMessage)
        return {
            type: 'UNKNOWN',
            message: defaultMessage
        };

    let statusCode: number | undefined;
    let errorMessage: string | undefined;

    if (err instanceof AxiosError) {
        const axiosError = err as AxiosError;
        delete axiosError.config?.headers.Authorization;

        const { status, response } = axiosError;

        logError(payload, response);

        statusCode = status;

        if (response) {
            const customError = parseToCustomError(response.data);

            if (customError)
                return customError;

            const { errors } = response.data as {
                errors?: string[]
            };

            if (errors && errors.length > 0)
                errorMessage = errors[0];
        }
    } else {
        logError(payload, err);
    }

    if ((statusCode && !ignoreStatusCodes.includes(statusCode)) || !errorMessage)
        Sentry.captureException(err);

    if (!errorMessage) {
        const { message } = err as {
            message?: string
        };

        errorMessage = 'Desculpe, ocorreu um erro inesperado. ';
        errorMessage += `Status code: ${statusCode || 'Desconhecido'}. `
        errorMessage += `Detalhe: ${message || 'Desconhecido'}.`;
    }

    return {
        message: errorMessage,
        type: 'UNKNOWN'
    };
};

const getMessageErrorByInvalidSession = ({ err, defaultMessage, user }: ExceptionSessionDetail): CustomError | null => {
    if (defaultMessage)
        return {
            type: 'UNKNOWN',
            message: defaultMessage
        };

    if (err instanceof AxiosError) {
        const { response } = err as AxiosError;

        if (response && response.status === 403) {
            if (
                user &&
                isBefore(typeof user.tokenExpiredDate === 'string' ? parseISO(user.tokenExpiredDate) : user.tokenExpiredDate, new Date())
            )
                return {
                    message: 'Sessão expirada!',
                    type: 'SESSION_EXPIRED'
                };

            const { errors } = response.data as {
                errors?: string[]
            };

            let errorMessage: string | undefined;

            if (errors && errors.length > 0)
                errorMessage = errors[0];
            else
                errorMessage = "Sessão inválida, logue novamente!"

            return {
                message: errorMessage,
                type: 'SESSION_INVALID'
            };
        }
    }

    return null;
};

const getCustomErrorByLandingPage = (
    { err, action, distributionRedemption, codes }: ExceptionLandingPageDetail
): CustomError => {
    let distribution: object = {};

    if (distributionRedemption) {
        const {
            distributionId,
            recipientId,
            recipientName,
            recipientEmail,
            recipientAmount,
            recipientHash,
            templateId,
            redeemedDate,
            expiredDate
        } = distributionRedemption;

        distribution = {
            id: distributionId,
            recipient: {
                id: recipientId,
                name: recipientName,
                email: recipientEmail,
                amount: recipientAmount,
                hash: recipientHash
            },
            templateId,
            redeemedDate,
            expiredDate
        };
    }

    const payload = {
        type: 'Landing Page',
        action,
        url: window.location.href,
        distribution,
        codes
    };

    const messageError = getMessageError({
        err,
        payload
    });

    return messageError;
};

export { 
    getMessageError,
    getMessageErrorByInvalidSession,
    getCustomErrorByLandingPage
};